import React from 'react'
import { useFieldArray, useWatch } from 'react-hook-form'
import { AudienceFilter } from './types'
import CombinatorSelector from './CombinatorSelector'
import { Button } from '@demandlocal/components'
import Rule from './Rule'

const RuleGroup = ({
  index,
  removeGroup,
}: {
  index: number
  removeGroup: (index: number) => void
}): React.ReactNode => {
  const {
    fields: rules,
    append: addRuleToGroup,
    remove,
  } = useFieldArray<AudienceFilter>({
    name: `groups.${index}.rules`,
  })

  const addRule = () => {
    if (rules.length > 0) {
      addRuleToGroup({
        type: 'combinator',
        value: 'and',
        label: 'AND',
      })
    }
    addRuleToGroup({
      type: 'filter',
    })
  }

  const handleRuleRemove = (index: number) => {
    const previousRule = rules?.[index - 1]
    const nextRule = rules?.[index + 1]
    const hasNextRule = rules?.[index + 2]
    const isFirst = index === 0
    const hasPreviousCombinator = previousRule?.type === 'combinator'
    const hasNextCombinator = nextRule?.type === 'combinator'

    const toRemove = [index]
    if (hasPreviousCombinator) toRemove.unshift(index - 1)
    if ((!hasNextRule && hasNextCombinator) || isFirst) toRemove.push(index + 1)

    remove(toRemove)
  }

  return (
    <div className="border-2 border-gainsboro-gray rounded-lg bg-cultured-gray">
      <div className="flex flex-col w-full">
        <div className="flex items-end justify-between px-4 pt-2">
          <div className="text-[10px] text-web-gray font-bold bg-gainsboro-gray w-5 h-5 flex items-center justify-center rounded-full">
            R
          </div>
          <span
            className="text-primary-persian-blue font-bold text-xs cursor-pointer"
            onClick={() => {
              removeGroup(index)
            }}>
            Delete rule
          </span>
        </div>

        <div className="pl-4">
          {rules.map((rule, ruleIndex) => {
            if (rule.type === 'combinator') {
              return (
                <CombinatorSelector
                  key={rule.id}
                  groupIndex={index}
                  ruleIndex={ruleIndex}
                  isInGroup
                />
              )
            }
            return (
              <Rule
                key={rule.id}
                groupIndex={index}
                ruleIndex={ruleIndex}
                handleRuleRemove={handleRuleRemove}
              />
            )
          })}
        </div>

        <div className="flex gap-4 p-4 pl-8">
          <Button
            animate={false}
            leftSlot={
              <div className="w-6 h-6 bg-primary-persian-blue rounded-full text-white text-center flex items-center justify-center">
                R
              </div>
            }
            type="button"
            variant="secondary"
            onClick={addRule}>
            + Add Rule
          </Button>
        </div>
      </div>
    </div>
  )
}

export default RuleGroup
